import { Modal, Flex } from 'antd';
import Icon from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useState, useEffect, useMemo } from 'react';
import styles from './business-hours-modal.module.scss';
import { Button } from '../button/button';
import { Checkbox } from '../checkbox/checkbox';
import { TimePicker } from '../time-picker/time-picker';
import PlusCircleIcon from '@/assets/icons/plus-circle.svg?react';
import TrashIcon from '@/assets/icons/trash-01.svg?react';
import InfoCircleIcon from '@/assets/icons/info-circle.svg?react';
import { useTranslation } from 'react-i18next';

type Period = {
  start: Dayjs | null;
  end: Dayjs | null;
};

type BusinessHoursModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (data: {
    periods: { start: string; end: string }[];
    isClosed: boolean;
    isAllDay: boolean;
  }) => void;
  selectedDate: Dayjs | null;
  initialPeriods?: { start: string; end: string }[];
  initialIsClosed?: boolean;
  initialIsAllDay?: boolean;
  isLoadingUpdateBusinessHours?: boolean;
};

// `dayjs().day()` order (Sunday first), keyed like the API's `wday`. Only used to pick the
// dictionary label, since `format('dddd')` follows the UI language.
const WEEKDAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

const DEFAULT_PERIOD: Period = {
  start: dayjs('09:00', 'HH:mm'),
  end: dayjs('17:00', 'HH:mm'),
};

export function BusinessHoursModal({
  open,
  onClose,
  onSave,
  selectedDate,
  initialPeriods,
  initialIsClosed = false,
  initialIsAllDay = false,
  isLoadingUpdateBusinessHours = false,
}: BusinessHoursModalProps) {
  const { t } = useTranslation(['businessHours', 'common']);
  const [periods, setPeriods] = useState<Period[]>([DEFAULT_PERIOD]);
  const [isClosed, setIsClosed] = useState(initialIsClosed);
  const [isAllDay, setIsAllDay] = useState(initialIsAllDay);

  useEffect(() => {
    if (open) {
      if (initialPeriods && initialPeriods.length > 0) {
        setPeriods(
          initialPeriods.map((p) => ({
            start: dayjs(p.start, 'HH:mm'),
            end: dayjs(p.end, 'HH:mm'),
          }))
        );
      } else {
        setPeriods([{ ...DEFAULT_PERIOD }]);
      }
      setIsClosed(initialIsClosed);
      setIsAllDay(initialIsAllDay);
    }
  }, [open, initialPeriods, initialIsClosed, initialIsAllDay]);

  const hasSameTime = useMemo(() => {
    return periods.some((period) => {
      if (!period.start || !period.end) return false;
      return period.start.isSame(period.end);
    });
  }, [periods]);

  const validatePeriods = useMemo(() => {
    type ErrorType = 'endBeforeStart' | 'overlap';
    const errors: {
      index: number;
      startInvalid: boolean;
      endInvalid: boolean;
      type: ErrorType;
    }[] = [];

    for (let i = 0; i < periods.length; i++) {
      const { start, end } = periods[i];

      if (start && end && end.isBefore(start)) {
        errors.push({ index: i, startInvalid: false, endInvalid: true, type: 'endBeforeStart' });
        continue;
      }
      if (i > 0) {
        const prevEnd = periods[i - 1].end;
        const startInvalid = !!(prevEnd && start && start.isBefore(prevEnd));
        const endInvalid = !!(prevEnd && end && end.isBefore(prevEnd));

        if (startInvalid || endInvalid) {
          errors.push({ index: i, startInvalid, endInvalid, type: 'overlap' });
        }
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }, [periods]);

  const hasChanges = useMemo(() => {
    if (isClosed !== initialIsClosed) return true;
    if (isAllDay !== initialIsAllDay) return true;

    const currentPeriods = periods
      .filter((p) => p.start && p.end)
      .map((p) => ({ start: p.start!.format('HH:mm'), end: p.end!.format('HH:mm') }));

    const initial = initialPeriods || [];

    if (currentPeriods.length !== initial.length) return true;

    for (let i = 0; i < currentPeriods.length; i++) {
      if (
        currentPeriods[i].start !== initial[i].start ||
        currentPeriods[i].end !== initial[i].end
      ) {
        return true;
      }
    }

    return false;
  }, [periods, isClosed, isAllDay, initialPeriods, initialIsClosed, initialIsAllDay]);

  const handleSave = () => {
    if (!validatePeriods.isValid) return;

    const formattedPeriods = periods
      .filter((p) => p.start && p.end)
      .map((p) => ({
        start: p.start!.format('HH:mm'),
        end: p.end!.format('HH:mm'),
      }));

    onSave({
      periods: isClosed || isAllDay ? [] : formattedPeriods,
      isClosed,
      isAllDay,
    });
    onClose();
  };

  const handleAddPeriod = () => {
    const lastPeriod = periods[periods.length - 1];
    const newStart = lastPeriod.end ? lastPeriod.end.add(1, 'hour') : dayjs('18:00', 'HH:mm');
    const newEnd = newStart.add(2, 'hour');
    setPeriods([...periods, { start: newStart, end: newEnd }]);
  };

  const handleRemovePeriod = (index: number) => {
    setPeriods(periods.filter((_, i) => i !== index));
  };

  const handlePeriodChange = (index: number, field: 'start' | 'end', value: Dayjs | null) => {
    const newPeriods = [...periods];
    newPeriods[index] = { ...newPeriods[index], [field]: value };
    setPeriods(newPeriods);
  };

  const handleClosedChange = (checked: boolean) => {
    if (checked) {
      setIsAllDay(false);
    }
    setIsClosed(checked);
  };

  const handleAllDayChange = (checked: boolean) => {
    if (checked) {
      setIsClosed(false);
    }
    setIsAllDay(checked);
  };

  if (!selectedDate) return null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={null}
      width={400}
      className={styles.modal}
    >
      <Flex vertical gap={20}>
        <div>
          <h3 className={styles.title}>
            {t('businessHours:modal.title', {
              weekday: t(`businessHours:weekdays.${WEEKDAY_KEYS[selectedDate.day()]}`),
              date: selectedDate.format('D MMM YYYY'),
            })}
          </h3>
          <p className={styles.subtitle}>{t('businessHours:modal.subtitle')}</p>
        </div>

        <Flex vertical gap={12}>
          {periods.map((period, index) => {
            const error = validatePeriods.errors.find((e) => e.index === index);
            const hasStartError = error?.startInvalid ?? false;
            const hasEndError = error?.endInvalid ?? false;
            const isFirstRow = index === 0;
            return (
              <Flex
                key={index}
                gap={12}
                align="center"
                className={isFirstRow ? styles.firstRow : undefined}
              >
                <Flex vertical gap={4} flex={1}>
                  {isFirstRow && <p className={styles.label}>{t('businessHours:modal.from')}</p>}
                  <TimePicker
                    value={period.start}
                    onChange={(value) => handlePeriodChange(index, 'start', value)}
                    disabled={isClosed || isAllDay}
                    status={hasStartError ? 'error' : undefined}
                  />
                </Flex>

                <Flex vertical gap={4} flex={1}>
                  {isFirstRow && <p className={styles.label}>{t('businessHours:modal.to')}</p>}
                  <TimePicker
                    value={period.end}
                    onChange={(value) => handlePeriodChange(index, 'end', value)}
                    disabled={isClosed || isAllDay}
                    status={hasEndError ? 'error' : undefined}
                  />
                </Flex>

                <Flex className={styles.actions} align="center">
                  {isFirstRow ? (
                    <Button
                      buttonVariant="tertiary"
                      className={styles.addButton}
                      onClick={handleAddPeriod}
                      disabled={isClosed || isAllDay}
                    >
                      <Icon component={() => <PlusCircleIcon className={styles.plusIcon} />} />
                    </Button>
                  ) : (
                    <Button
                      buttonVariant="tertiary"
                      className={styles.removeButton}
                      onClick={() => handleRemovePeriod(index)}
                      disabled={isClosed || isAllDay}
                    >
                      <Icon component={() => <TrashIcon className={styles.trashIcon} />} />
                    </Button>
                  )}
                </Flex>
              </Flex>
            );
          })}
          {validatePeriods.errors.some((e) => e.type === 'endBeforeStart') && (
            <p className={styles.errorText}>{t('businessHours:validation.endBeforeStart')}</p>
          )}
          {validatePeriods.errors.some((e) => e.type === 'overlap') && (
            <p className={styles.errorText}>{t('businessHours:validation.overlap')}</p>
          )}
          {hasSameTime && !validatePeriods.errors.length && (
            <Flex gap={8} align="center" className={styles.infoText}>
              <Icon component={() => <InfoCircleIcon className={styles.infoIcon} />} />
              <span>{t('businessHours:validation.sameTime')}</span>
            </Flex>
          )}
        </Flex>

        <Flex vertical gap={12}>
          <Checkbox checked={isClosed} onChange={(e) => handleClosedChange(e.target.checked)}>
            {t('businessHours:modal.closedAllDay')}
          </Checkbox>

          <Checkbox checked={isAllDay} onChange={(e) => handleAllDayChange(e.target.checked)}>
            {t('businessHours:modal.openAllDay')}
          </Checkbox>
        </Flex>

        <Flex gap={12} justify="flex-end">
          <Button buttonVariant="secondary" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            buttonVariant="primary"
            onClick={handleSave}
            loading={isLoadingUpdateBusinessHours}
            disabled={!hasChanges || !validatePeriods.isValid}
          >
            {t('common:actions.save')}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
}
